import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICES } from "@/data/site";

const AGES = ["Prefer not to say", "18–24", "25–34", "35–44", "45–54", "55–64", "65+"];
const INS = ["Aetna", "Cigna", "United / Oxford", "Blue Cross Blue Shield", "Anthem", "Humana", "Kaiser", "Other", "Out-of-network / Self-pay", "Not sure yet"];

export default function EnrollForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", ageRange: "", concern: "", insurance: "", memberId: "", preferredContact: "email", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone || !f.concern) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enroll", f);
      setDone(true);
      toast.success("Received — we'll reach out within 1 business day.");
    } catch {
      toast.error("Something went wrong. Please call us at 203-437-8193.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="enroll-success" className="border border-border bg-white p-12 text-center">
        <h3 className="font-serif text-3xl text-ink">Thank you.</h3>
        <p className="mt-4 font-sans text-base leading-relaxed text-ink-soft">
          Your request is in. A care coordinator will call or email within one business day. A confirmation is on its way to your inbox.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="enroll-form" className="border border-border bg-white p-8 sm:p-10">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label className="font-sans text-xs text-ink-soft">Full name *</Label>
          <Input data-testid="enroll-name" value={f.name} onChange={set("name")} className="mt-1.5 rounded-none border-border" />
        </div>
        <div>
          <Label className="font-sans text-xs text-ink-soft">Email *</Label>
          <Input data-testid="enroll-email" type="email" value={f.email} onChange={set("email")} className="mt-1.5 rounded-none border-border" />
        </div>
        <div>
          <Label className="font-sans text-xs text-ink-soft">Phone *</Label>
          <Input data-testid="enroll-phone" value={f.phone} onChange={set("phone")} className="mt-1.5 rounded-none border-border" />
        </div>
        <div>
          <Label className="font-sans text-xs text-ink-soft">Age range</Label>
          <Select value={f.ageRange} onValueChange={set("ageRange")}>
            <SelectTrigger data-testid="enroll-age" className="mt-1.5 rounded-none border-border"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{AGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-5">
        <Label className="font-sans text-xs text-ink-soft">Primary concern *</Label>
        <Select value={f.concern} onValueChange={set("concern")}>
          <SelectTrigger data-testid="enroll-concern" className="mt-1.5 rounded-none border-border"><SelectValue placeholder="What brings you in?" /></SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => <SelectItem key={s.slug} value={s.name}>{s.short}</SelectItem>)}
            <SelectItem value="Not sure yet">Not sure yet</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <div>
          <Label className="font-sans text-xs text-ink-soft">Insurance (optional)</Label>
          <Select value={f.insurance} onValueChange={set("insurance")}>
            <SelectTrigger data-testid="enroll-insurance" className="mt-1.5 rounded-none border-border"><SelectValue placeholder="Skip if unsure" /></SelectTrigger>
            <SelectContent>{INS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="font-sans text-xs text-ink-soft">Member ID (optional)</Label>
          <Input data-testid="enroll-member" value={f.memberId} onChange={set("memberId")} className="mt-1.5 rounded-none border-border" />
        </div>
      </div>

      <div className="mt-5">
        <Label className="font-sans text-xs text-ink-soft">Anything else you'd like us to know?</Label>
        <Textarea data-testid="enroll-message" value={f.message} onChange={set("message")} rows={4} className="mt-1.5 rounded-none border-border" />
      </div>

      <p className="mt-5 font-sans text-xs leading-relaxed text-muted-foreground">
        By submitting, you agree to be contacted about scheduling. Your information is protected under HIPAA.
      </p>
      <button data-testid="enroll-submit" disabled={loading} className="mt-6 w-full rounded-full bg-ink py-4 font-sans text-sm text-linen transition-colors hover:bg-ink/85 disabled:opacity-60">
        {loading ? "Sending…" : "Submit enrollment"}
      </button>
    </form>
  );
}
